import { useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { Upload } from "lucide-react";
import {
  CredentialDocumentIntent,
  CredentialType,
} from "@api/shared-runtime/enums";
import { CREDENTIAL_DOCUMENT_UPLOAD_EXTENSIONS } from "@api/shared-runtime/credential-document-upload";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { UploadDocuments } from "@/components/UploadDocuments";
import { Button } from "@/components/ui/button";
import type { Credential } from "@/features/credentials/types";
import { cn } from "@/lib/utils";
import type { useCredentialVaultReview } from "./use-credential-vault-review";

const DOCUMENT_UPLOAD_HEIGHT = 160;
const FILE_DRAG_TYPE = "Files";

function isFileDrag(event: DragEvent<HTMLElement>) {
  return (
    Array.from(event.dataTransfer.types).includes(FILE_DRAG_TYPE) ||
    event.dataTransfer.files.length > 0
  );
}

export function ProviderCredentialUpload({
  credential,
  userId,
  organizationBusinessUnitId,
  expectedAffiliationId,
  review,
  disabled,
  isEditing,
  compact = false,
  actions,
}: {
  credential?: Credential;
  userId: string;
  organizationBusinessUnitId?: string;
  expectedAffiliationId?: string;
  review: Pick<
    ReturnType<typeof useCredentialVaultReview>,
    "isUploadOpen" | "setUploadOpen" | "uploadState" | "filesUploaded"
  > & {
    setUploadState: (state: {
      isUploading: boolean;
      pendingFileCount: number;
    }) => void;
    query: { refetch: () => Promise<unknown> };
  };
  disabled: boolean;
  isEditing: boolean;
  compact?: boolean;
  actions?: ReactNode;
}) {
  const uploadId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selection, setSelection] = useState({ files: [] as File[], batch: 0 });
  const isUploadDisabled =
    disabled ||
    review.uploadState.isUploading ||
    review.uploadState.pendingFileCount > 0;
  const isPersonalInfo =
    credential?.credentialType === CredentialType.PersonalInfo;
  let title = "Start with a credential file";
  if (!credential) {
    title = "Upload credential files";
  } else if (isPersonalInfo) {
    title = "Add a supporting document";
  } else if (isEditing) {
    title = "Update your credential with a file";
  }
  const uploadHint =
    isDragging && !isUploadDisabled
      ? "Drop your files here"
      : "Drag and drop, or choose a file.";
  let description =
    "We’ll fill in details we can read for you to review before saving. You can also upload a file as supporting evidence.";
  if (isPersonalInfo) {
    description =
      "Upload a file to support your details. Review any information we extract before saving.";
  }
  if (compact) {
    description = uploadHint;
  }

  function selectFiles(files: File[]) {
    if (isUploadDisabled || files.length === 0) {
      return;
    }
    // The uploader is lazy-loaded, so reserve this batch immediately to keep
    // Save and close actions locked until its real queue takes over.
    review.setUploadState({
      isUploading: true,
      pendingFileCount: files.length,
    });
    setSelection((previous) => ({ files, batch: previous.batch + 1 }));
    review.setUploadOpen(true);
  }

  function handleDragOver(event: DragEvent<HTMLElement>) {
    if (!isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = isUploadDisabled ? "none" : "copy";
    setIsDragging(!isUploadDisabled);
  }

  function handleDrop(event: DragEvent<HTMLElement>) {
    if (!isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    selectFiles(Array.from(event.dataTransfer.files));
  }

  const uploadButton = (
    <Button
      type="button"
      className={cn(
        PROFILE_FORM_STYLES.primaryButton,
        "w-full shrink-0 gap-2 sm:w-auto",
        compact && "h-10 w-auto px-3 text-xs sm:text-sm",
      )}
      disabled={isUploadDisabled}
      onClick={() => fileInputRef.current?.click()}
    >
      <Upload className="size-4" aria-hidden="true" />
      Upload a file
    </Button>
  );

  return (
    <div className="space-y-4">
      <section
        aria-labelledby={`${uploadId}-title`}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={(event) => {
          if (
            event.relatedTarget instanceof Node &&
            event.currentTarget.contains(event.relatedTarget)
          ) {
            return;
          }
          setIsDragging(false);
        }}
        onDrop={handleDrop}
        className={cn(
          PROFILE_FORM_STYLES.section,
          "border-dashed border-primary/30 bg-primary/5 shadow-none transition-colors",
          compact && "space-y-0 p-3 sm:p-4",
          isDragging &&
            !isUploadDisabled &&
            "border-primary bg-primary/10 ring-2 ring-primary/20",
        )}
      >
        <div
          className={cn(
            "flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6",
            compact && "flex-row items-center justify-between gap-3 sm:gap-4",
            compact &&
              actions &&
              "flex-col items-stretch sm:flex-col sm:items-stretch",
          )}
        >
          <div className="flex items-start gap-3">
            <div
              className={cn(
                "flex size-10 shrink-0 items-center justify-center rounded-xl bg-white text-primary",
                compact && "hidden sm:flex sm:size-9",
              )}
            >
              <Upload className="size-5" aria-hidden="true" />
            </div>
            <div className={cn("min-w-0 space-y-2", compact && "space-y-1")}>
              {credential && !isPersonalInfo && (
                <p className={PROFILE_FORM_STYLES.eyebrow}>Recommended</p>
              )}
              <h3
                id={`${uploadId}-title`}
                className={cn(
                  "text-base font-semibold leading-snug text-slate-900",
                  compact && "text-sm",
                )}
              >
                {title}
              </h3>
              <p
                role={compact ? "status" : undefined}
                className={cn(
                  "text-sm leading-relaxed text-slate-600",
                  compact && "text-xs",
                )}
              >
                {description}
              </p>
              {!compact && (
                <p
                  role="status"
                  className="text-xs font-medium leading-relaxed text-primary"
                >
                  {isDragging && !isUploadDisabled
                    ? "Drop your files here"
                    : "Drag and drop files here, or choose Upload a file."}
                </p>
              )}
            </div>
          </div>
          {actions ? (
            <div className="flex flex-wrap items-center gap-2">
              {uploadButton}
              {actions}
            </div>
          ) : (
            uploadButton
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={CREDENTIAL_DOCUMENT_UPLOAD_EXTENSIONS.join(",")}
          aria-label={
            credential ? "Choose credential files" : "Choose vault files"
          }
          className="hidden"
          disabled={isUploadDisabled}
          onChange={(event) => {
            selectFiles(Array.from(event.currentTarget.files ?? []));
            event.currentTarget.value = "";
          }}
        />
      </section>
      {review.isUploadOpen && (
        <div>
          <UploadDocuments
            key={selection.batch}
            initialFiles={selection.files}
            autoUpload
            minDocuments={1}
            height={DOCUMENT_UPLOAD_HEIGHT}
            user={{ id: userId }}
            // In org mode the upload adapter uses this ID as the selected provider.
            // organizationBusinessUnitId selects the org-only review transport.
            switchUserId={userId}
            organizationBusinessUnitId={organizationBusinessUnitId}
            expectedAffiliationId={expectedAffiliationId}
            intent={CredentialDocumentIntent.Review}
            targetCredentialId={credential?.credentialId ?? undefined}
            onUploadStateChange={review.setUploadState}
            onReviewDocumentsUploaded={review.filesUploaded}
            onFilesProcessed={async () => {
              await review.query.refetch();
            }}
            onFilesUploaded={() => review.setUploadOpen(false)}
          />
        </div>
      )}
      {review.uploadState.pendingFileCount > 0 && (
        <p role="status" className="text-sm text-slate-600">
          {credential
            ? "Finish uploading or remove the remaining files before saving."
            : "Finish uploading or remove the remaining files before closing the vault."}
        </p>
      )}
    </div>
  );
}
